import { useEffect, useState, useMemo, createContext, useContext, useCallback } from "react";
import "@/App.css";
import "@/i18n";
import i18n from "@/i18n";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { trackPageView, analyticsEvents } from "@/utils/analytics";
import { setUser as setSentryUser, clearUser as clearSentryUser } from "@/utils/sentry";

// Pages
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Restaurants from "@/pages/Restaurants";
import Preferences from "@/pages/Preferences";
import Leaderboard from "@/pages/Leaderboard";
import Profile from "@/pages/Profile";
import RestaurantPortal from "@/pages/RestaurantPortal";
import AuthPage from "@/pages/AuthPage";
import Community from "@/pages/Community";
import SharedProfile from "@/pages/SharedProfile";
import Analytics from "@/pages/Analytics";
import RestaurantMap from "@/pages/RestaurantMap";
import Wishlist from "@/pages/Wishlist";
import Achievements from "@/pages/Achievements";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminUserDetail from "@/pages/AdminUserDetail";
import AutoLogin from "@/pages/AutoLogin";
import Collections from "@/pages/Collections";
import CollectionDetail from "@/pages/CollectionDetail";
import SharedCollection from "@/pages/SharedCollection";
import SharedVisit from "@/pages/SharedVisit";
import Goals from "@/pages/Goals";
import Wrapped from "@/pages/Wrapped";

// Components
import CookieConsent from "@/components/CookieConsent";
import ErrorBoundary from "@/components/ErrorBoundary";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      // Set language from user profile if available
      if (response.data.language && response.data.language !== i18n.language) {
        i18n.changeLanguage(response.data.language);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios({
      method: 'post',
      url: `${API}/auth/login`,
      data: { email, password },
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' }
    });
    const { user: userData } = response.data;
    setUser(userData);
    
    // Track login for analytics and error reporting
    setSentryUser(userData);
    analyticsEvents.userLoggedIn();
    
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await axios({
      method: 'post',
      url: `${API}/auth/register`,
      data: { name, email, password },
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' }
    });
    const { user: userData } = response.data;
    setUser(userData);
    
    // Track registration for analytics and error reporting
    setSentryUser(userData);
    analyticsEvents.userRegistered();
    
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.warn("Logout error:", error);
    }
    setUser(null);
    
    // Clear user from error tracking
    clearSentryUser();
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const authValue = useMemo(
    () => ({ user, loading, login, register, logout, checkAuth, updateUser }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading]
  );

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

// App Router with Analytics
const AppRouter = () => {
  const location = useLocation();
  
  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);
  
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/restaurants" element={
        <ProtectedRoute>
          <Restaurants />
        </ProtectedRoute>
      } />
      <Route path="/preferences" element={
        <ProtectedRoute>
          <Preferences />
        </ProtectedRoute>
      } />
      <Route path="/leaderboard" element={
        <ProtectedRoute>
          <Leaderboard />
        </ProtectedRoute>
      } />
      <Route path="/community" element={
        <ProtectedRoute>
          <Community />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <Analytics />
        </ProtectedRoute>
      } />
      <Route path="/map" element={
        <ProtectedRoute>
          <RestaurantMap />
        </ProtectedRoute>
      } />
      <Route path="/wishlist" element={
        <ProtectedRoute>
          <Wishlist />
        </ProtectedRoute>
      } />
      <Route path="/achievements" element={
        <ProtectedRoute>
          <Achievements />
        </ProtectedRoute>
      } />
      <Route path="/collections" element={
        <ProtectedRoute>
          <Collections />
        </ProtectedRoute>
      } />
      <Route path="/collections/:collectionId" element={
        <ProtectedRoute>
          <CollectionDetail />
        </ProtectedRoute>
      } />
      <Route path="/collections/shared/:shareCode" element={<SharedCollection />} />
      <Route path="/visits/shared/:shareCode" element={<SharedVisit />} />
      <Route path="/goals" element={
        <ProtectedRoute>
          <Goals />
        </ProtectedRoute>
      } />
      <Route path="/wrapped" element={
        <ProtectedRoute>
          <Wrapped />
        </ProtectedRoute>
      } />
      <Route path="/wrapped/:year" element={
        <ProtectedRoute>
          <Wrapped />
        </ProtectedRoute>
      } />
      <Route path="/portal" element={<RestaurantPortal />} />
      <Route path="/restaurant-portal" element={<RestaurantPortal />} />
      <Route path="/profile/share/:userId" element={<SharedProfile />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/users/:userId" element={<AdminUserDetail />} />
      
      {/* Auto-Login Route */}
      <Route path="/auto-login" element={<AutoLogin />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-right" />
            <CookieConsent />
          </AuthProvider>
        </BrowserRouter>
        <div className="noise-overlay" />
      </div>
    </ErrorBoundary>
  );
}

export default App;
export { API };
